import React, { useState } from "react";
import { Popover, CircularProgress } from "@mui/material";
import axiosInstant from "../../../api";
import {
  expertTeamSingleProEndpoint,
  expertProjectConfirmationEndpoint,
} from "../../../api/endpoints";
import { buildExpertAvailability } from "../helper";
import {
  IconPhone,
  IconMail,
  IconPin,
  IconCalendar,
  IconCopy,
  IconCheck,
  IconContact,
} from "../../../utils/icons";
import { getStateLabel } from "../../pro/createEditProfile/formFragments/constants";

const clean = (value) => (typeof value === "string" ? value.trim() : value || "");

const buildAddress = (src) =>
  [
    clean(src?.address1),
    clean(src?.address2),
    [clean(src?.city), getStateLabel(clean(src?.state))].filter(Boolean).join(", "),
    clean(src?.zipcode),
  ]
    .filter(Boolean)
    .join(", ");

const ExpertContactPopover = ({ pro, project }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(null);
  const [details, setDetails] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    if (fetched || loading || !pro?.userid) return;
    setLoading(true);
    const tasks = [
      axiosInstant
        .get(expertTeamSingleProEndpoint(pro.userid))
        .then((res) => setDetails(res.data || {}))
        .catch(() => setDetails({})),
    ];
    if (project?.id) {
      tasks.push(
        axiosInstant
          .get(expertProjectConfirmationEndpoint(project.id, pro.userid))
          .then((res) => setConfirmation(res.data?.confirmation || {}))
          .catch(() => setConfirmation({}))
      );
    }
    await Promise.allSettled(tasks);
    setFetched(true);
    setLoading(false);
  };

  const handleCopy = (label, value) => {
    navigator.clipboard?.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const src = { ...pro, ...(details || {}) };
  const phone = clean(src?.phoneNumber) || clean(src?.agencyPhoneNumber);
  const email = clean(src?.emailAddress) || clean(src?.agencyEmail);
  const address = buildAddress(src);
  const availability = buildExpertAvailability(
    confirmation,
    pro?.projects?.[0]?.BrandProjectPro
  );

  const rows = [
    {
      key: "phone",
      label: "Phone",
      icon: <IconPhone width={14} height={14} stroke="#AB854A" />,
      value: phone,
      href: phone ? `tel:${phone}` : null,
      copyable: true,
    },
    {
      key: "email",
      label: "Email",
      icon: <IconMail width={14} height={14} stroke="#AB854A" strokeWidth={1.9} />,
      value: email,
      href: email ? `mailto:${email}` : null,
      copyable: true,
    },
    {
      key: "availability",
      label: "Availability",
      icon: <IconCalendar width={14} height={14} stroke="#AB854A" />,
      value: availability,
      href: null,
      copyable: false,
    },
    {
      key: "address",
      label: "Address",
      icon: <IconPin width={14} height={14} stroke="#AB854A" />,
      value: address,
      href: null,
      copyable: true,
    },
  ];

  const open = Boolean(anchorEl);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center gap-[6px] rounded-[9px] border px-[13px] py-[6px] font-circular text-[13px] font-[600] text-accent-text transition-colors ${
          open
            ? "border-[#E0CFA8] bg-tint"
            : "border-border-token bg-surface-2 hover:bg-tint"
        }`}
      >
        <IconContact width={15} height={15} stroke="#AB854A" />
        Contact
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: "8px",
              width: 270,
              borderRadius: "13px",
              border: "1px solid var(--border)",
              boxShadow: "0 14px 36px rgba(0,0,0,0.16)",
            },
          },
        }}
      >
        <div className="px-3 font-circular">
          <div className="border-b border-border-token py-[9px] text-[11px] font-[700] uppercase tracking-[0.5px] text-accent-text">
            Contact Details
          </div>
          {loading && !fetched ? (
            <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-text-2">
              <CircularProgress size={16} sx={{ color: "var(--accent)" }} />
              Loading…
            </div>
          ) : (
            rows.map((row) => (
              <div
                key={row.key}
                className="flex items-center gap-[10px] border-b border-border-token py-[9px] last:border-b-0"
              >
                <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[7px] border border-border-token bg-surface-2">
                  {row.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] font-[600] uppercase tracking-[0.3px] text-text-2">
                    {row.label}
                  </div>
                  {row.value ? (
                    row.href ? (
                      <a
                        href={row.href}
                        className="mt-[1px] block break-words text-[13.5px] font-[500] text-text-1 no-underline hover:text-accent-text hover:underline"
                      >
                        {row.value}
                      </a>
                    ) : (
                      <span className="mt-[1px] block break-words text-[13.5px] font-[500] text-text-1">
                        {row.value}
                      </span>
                    )
                  ) : (
                    <span className="mt-[1px] block text-[13px] italic text-muted">
                      Not provided
                    </span>
                  )}
                </div>
                {row.copyable && row.value && (
                  <button
                    type="button"
                    title={`Copy ${row.label.toLowerCase()}`}
                    onClick={() => handleCopy(row.label, row.value)}
                    className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[6px] text-muted hover:bg-tint hover:text-accent-text"
                  >
                    {copied === row.label ? (
                      <IconCheck width={14} height={14} stroke="#1C7A5A" />
                    ) : (
                      <IconCopy width={13} height={13} />
                    )}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </Popover>
    </>
  );
};

export default ExpertContactPopover;
